import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import {
    evenPercents,
    hangTemplate,
    imageCellsOf,
    isPanelCell,
    locateViewport,
    panelCellsOf,
    panelKindOfCell,
    planesOfRow,
    rowCanRotate,
    sameShape,
    templateOfRow,
} from './layout';
import {
    DatasetRef,
    DatasetRow,
    GridRowSpec,
    Layout,
    PanelBand,
    panelContent,
    PanelKind,
    PRIMARY_ROW_INDEX,
    RowFusion,
    RowLock,
    RowTemplate,
    ViewportCell,
    ViewportId,
    ViewportIdFactory,
} from './types';

/** The share of a row's height a band takes when it first hangs. */
const PANEL_BAND_HEIGHT_PERCENT = 35;

/**
 * Pure, immutable layout transforms — the model side of the grid's
 * interactions. None mutate the input; each returns a new `Layout`. Out-of-
 * range / unknown targets and mismatched size arrays are no-ops (the layout
 * is returned unchanged) rather than throwing, so a stray UI event can never
 * corrupt the layout. "A row never mixes datasets" holds by construction.
 */

function replaceRow(layout: Layout, rowIndex: number, row: DatasetRow): Layout {
    return {
        rows: layout.rows.map((existing, index) =>
            index === rowIndex ? row : existing
        ),
    };
}

function withEvenHeights(rows: ReadonlyArray<DatasetRow>): Layout {
    const heights = evenPercents(rows.length);
    return {
        rows: rows.map((row, index) => ({
            ...row,
            heightPercent: heights[index],
        })),
    };
}

/**
 * Inserts a new dataset row (bound, or an empty placeholder) at `index`,
 * pushing the rows below it down. Heights re-distribute evenly.
 */
export function insertRow(
    layout: Layout,
    index: number,
    spec: GridRowSpec,
    createId: ViewportIdFactory
): Layout {
    if (index < 0 || index > layout.rows.length) {
        return layout;
    }
    const row: DatasetRow = {
        dataset: spec.dataset,
        heightPercent: 0,
        ...hangTemplate(spec.template, createId),
    };
    const rows = [...layout.rows];
    rows.splice(index, 0, row);
    return withEvenHeights(rows);
}

/**
 * Removes a whole dataset row; heights re-distribute evenly. The primary row
 * is never removed: it is the row that shows the dataset the sidebar names.
 */
export function removeRow(layout: Layout, rowIndex: number): Layout {
    if (
        rowIndex === PRIMARY_ROW_INDEX ||
        rowIndex < 0 ||
        rowIndex >= layout.rows.length
    ) {
        return layout;
    }
    return withEvenHeights(
        layout.rows.filter((_, index) => index !== rowIndex)
    );
}

/**
 * Shows `datasets` one row each: keeps the primary row and every row whose
 * dataset is among them (panes, pair and shape as they are, two rows of one
 * dataset both kept), removes every other row, empty placeholders included,
 * and appends a row hung as `template` for each dataset no row shows yet, in
 * the order given. Heights re-distribute evenly; when the rows already show
 * exactly `datasets`, the layout comes back unchanged.
 */
export function showImageRows(
    layout: Layout,
    datasets: ReadonlyArray<DatasetRef>,
    template: RowTemplate,
    createId: ViewportIdFactory
): Layout {
    const wanted = new Set(datasets.map((d) => d.seriesInstanceUid));
    const kept = layout.rows.filter(
        (row, index) =>
            index === PRIMARY_ROW_INDEX ||
            (row.dataset !== undefined &&
                wanted.has(row.dataset.seriesInstanceUid))
    );
    const shown = new Set(
        kept.flatMap((row) =>
            row.dataset ? [row.dataset.seriesInstanceUid] : []
        )
    );
    const added = datasets.filter(
        (dataset, index) =>
            !shown.has(dataset.seriesInstanceUid) &&
            datasets.findIndex(
                (d) => d.seriesInstanceUid === dataset.seriesInstanceUid
            ) === index
    );
    if (kept.length === layout.rows.length && added.length === 0) {
        return layout;
    }
    return withEvenHeights([
        ...kept,
        ...added.map((dataset) => ({
            dataset,
            heightPercent: 0,
            ...hangTemplate(template, createId),
        })),
    ]);
}

/**
 * Empties each bound row whose dataset `isAvailable` refuses, back to the
 * placeholder a picker binds into: its panes and its band go, its height
 * stays. The primary row empties too, since it must never show an image
 * that is gone. The layout itself comes back when every bound dataset is
 * available.
 */
export function unbindUnavailableDatasets(
    layout: Layout,
    isAvailable: (seriesInstanceUid: SeriesInstanceUid) => boolean
): Layout {
    let changed = false;
    const rows = layout.rows.map((row): DatasetRow => {
        if (!row.dataset || isAvailable(row.dataset.seriesInstanceUid)) {
            return row;
        }
        changed = true;
        return emptiedRow(row);
    });
    return changed ? { rows } : layout;
}

/**
 * Empties one row back to the placeholder: its panes and its band go, its
 * height stays. The layout itself comes back for a row that is already
 * empty or does not exist.
 */
export function unbindRow(layout: Layout, rowIndex: number): Layout {
    const target = layout.rows[rowIndex];
    if (!target?.dataset) {
        return layout;
    }
    return {
        rows: layout.rows.map((row, index) =>
            index === rowIndex ? emptiedRow(row) : row
        ),
    };
}

/** The placeholder a bound row empties to, at the row's own height. */
function emptiedRow(row: DatasetRow): DatasetRow {
    return {
        dataset: undefined,
        heightPercent: row.heightPercent,
        columnPercents: [],
        rowPercents: [],
        cells: [],
    };
}

/** Commits row heights (e.g. from a vertical resize). */
export function setRowHeights(layout: Layout, heights: number[]): Layout {
    if (heights.length !== layout.rows.length) {
        return layout;
    }
    return {
        rows: layout.rows.map((row, index) => ({
            ...row,
            heightPercent: heights[index],
        })),
    };
}

/** Commits a row's column track sizes (e.g. from a horizontal resize). */
export function setColumnWidths(
    layout: Layout,
    rowIndex: number,
    widths: number[]
): Layout {
    const row = layout.rows[rowIndex];
    if (!row || widths.length !== row.columnPercents.length) {
        return layout;
    }
    return replaceRow(layout, rowIndex, { ...row, columnPercents: widths });
}

/**
 * Commits a row's sub-row track sizes: the split between panes stacked in
 * one column, such as the sagittal above the coronal.
 */
export function setSubRowHeights(
    layout: Layout,
    rowIndex: number,
    heights: number[]
): Layout {
    const row = layout.rows[rowIndex];
    if (!row || heights.length !== row.rowPercents.length) {
        return layout;
    }
    return replaceRow(layout, rowIndex, { ...row, rowPercents: heights });
}

/**
 * Reorders a viewport within its row to a new position in reading order
 * (drag-rearrange). Datasets stay on their row — there is no cross-row move,
 * and no move between the image panes and the band: a panel reorders among
 * the panels, an image pane among the image panes. Placements are
 * positional: the moved pane takes the destination's slot, so the row keeps
 * its shape and only the contents move.
 */
export function reorderViewport(
    layout: Layout,
    viewportId: ViewportId,
    toColumnIndex: number
): Layout {
    const located = locateViewport(layout, viewportId);
    if (!located) {
        return layout;
    }
    const { rowIndex, row, cell } = located;
    const fromIndex = row.cells.findIndex(
        (candidate) => candidate.id === viewportId
    );
    const target = Math.max(0, Math.min(row.cells.length - 1, toColumnIndex));
    if (
        fromIndex === target ||
        isPanelCell(row.cells[target]) !== isPanelCell(cell)
    ) {
        return layout;
    }
    const positionalPlacements = row.cells.map((cell) => cell.placement);
    const reordered = [...row.cells];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(target, 0, moved);
    return replaceRow(layout, rowIndex, {
        ...row,
        cells: reordered.map((cell, index) => ({
            ...cell,
            placement: positionalPlacements[index],
        })),
    });
}

/**
 * Advances a row's plane panes one step through the planes they already show,
 * in reading order: the first pane takes the second's plane, the last takes
 * the first's. Each pane keeps its id, its place and its image layer, so the
 * panes swap what they draw instead of being rebuilt and no renderer is
 * re-minted.
 *
 * A row that cannot rotate (see {@link rowCanRotate}) is returned unchanged.
 */
export function rotateRowPlanes(layout: Layout, rowIndex: number): Layout {
    const row = layout.rows[rowIndex];
    if (!row || !rowCanRotate(row)) {
        return layout;
    }
    const planes = planesOfRow(row);
    let paneIndex = 0;
    return replaceRow(layout, rowIndex, {
        ...row,
        cells: row.cells.map((cell) => {
            if (cell.content.kind !== 'plane') {
                return cell;
            }
            const next = planes[(paneIndex + 1) % planes.length];
            paneIndex += 1;
            return { ...cell, content: { ...cell.content, plane: next } };
        }),
    });
}

/**
 * Binds a dataset to a row. An empty (placeholder) row gets fresh panes from
 * the template; a row that already has panes keeps them and only swaps the
 * dataset (re-bind). Which datasets may be bound is the caller's question;
 * this is the pure layout transform.
 */
export function bindDataset(
    layout: Layout,
    rowIndex: number,
    dataset: DatasetRef,
    template: RowTemplate,
    createId: ViewportIdFactory
): Layout {
    const row = layout.rows[rowIndex];
    if (!row) {
        return layout;
    }
    // A lock or a fusion chosen for one image says nothing about another.
    if (row.cells.length > 0) {
        return replaceRow(layout, rowIndex, {
            ...row,
            dataset,
            lock: undefined,
            fusion: undefined,
        });
    }
    return replaceRow(layout, rowIndex, {
        ...row,
        dataset,
        lock: undefined,
        fusion: undefined,
        ...hangTemplate(template, createId),
    });
}

/** Sets how one bound row lies on the primary row; the layout itself for an unbound or missing row. */
export function setRowLock(
    layout: Layout,
    rowIndex: number,
    lock: RowLock
): Layout {
    const row = layout.rows[rowIndex];
    if (!row?.dataset) {
        return layout;
    }
    return replaceRow(layout, rowIndex, { ...row, lock });
}

/**
 * Sets, or with `undefined` drops, one bound row's own fusion; the layout
 * itself for the primary row, whose fusion is its image's, or for an
 * unbound or missing row.
 */
export function setRowFusion(
    layout: Layout,
    rowIndex: number,
    fusion: RowFusion | undefined
): Layout {
    const row = layout.rows[rowIndex];
    if (!row?.dataset || rowIndex === PRIMARY_ROW_INDEX) {
        return layout;
    }
    return replaceRow(layout, rowIndex, { ...row, fusion });
}

/**
 * Hangs a row's image panes in a new shape, the dataset, row height and
 * band kept. The first image pane keeps its id, so the tile it drew stays
 * mounted and the row's dataset never drops to zero tiles on the way. Every
 * other image pane is minted fresh, so no renderer outlives the pane it
 * drew. The band's panels stay as they are, after the new image panes.
 */
export function rehangRow(
    layout: Layout,
    rowIndex: number,
    template: RowTemplate,
    createId: ViewportIdFactory
): Layout {
    const row = layout.rows[rowIndex];
    if (!row) {
        return layout;
    }
    const hung = hangTemplate(template, createId);
    const keptId = imageCellsOf(row)[0]?.id;
    const cells =
        keptId === undefined
            ? hung.cells
            : hung.cells.map((cell, index) =>
                  index === 0 ? { ...cell, id: keptId } : cell
              );
    return replaceRow(layout, rowIndex, {
        ...row,
        ...hung,
        cells: [...cells, ...panelCellsOf(row)],
    });
}

/**
 * Hangs every bound row in one shape. A row already in that shape is left as
 * it is, its sizes included; every other bound row is re-hung the way
 * {@link rehangRow} does. An unbound placeholder row takes no panes: it hangs
 * the grid's shape once a dataset is bound into it. The layout itself comes
 * back when no row had to change.
 */
export function rehangRows(
    layout: Layout,
    template: RowTemplate,
    createId: ViewportIdFactory
): Layout {
    let next = layout;
    layout.rows.forEach((row, rowIndex) => {
        if (row.dataset && !sameShape(templateOfRow(row), template)) {
            next = rehangRow(next, rowIndex, template, createId);
        }
    });
    return next;
}

/**
 * Commits a row's band height, in percent of the row (e.g. from the drag of
 * the gutter above the band). A row without a band is left as it is.
 */
export function setBandHeight(
    layout: Layout,
    rowIndex: number,
    heightPercent: number
): Layout {
    const row = layout.rows[rowIndex];
    if (!row?.band) {
        return layout;
    }
    return replaceRow(layout, rowIndex, {
        ...row,
        band: { ...row.band, heightPercent },
    });
}

/** Commits a row's band column track sizes (e.g. from a horizontal resize). */
export function setBandColumnWidths(
    layout: Layout,
    rowIndex: number,
    widths: number[]
): Layout {
    const row = layout.rows[rowIndex];
    if (!row?.band || widths.length !== row.band.columnPercents.length) {
        return layout;
    }
    return replaceRow(layout, rowIndex, {
        ...row,
        band: { ...row.band, columnPercents: widths },
    });
}

/** The row with its band and every panel taken out; the row itself without one. */
function withoutBand(row: DatasetRow): DatasetRow {
    if (!row.band && !row.cells.some(isPanelCell)) {
        return row;
    }
    return { ...row, band: undefined, cells: imageCellsOf(row) };
}

/**
 * The band's column sizes after its panels went from `before` to `after`
 * kinds: a kept column keeps its share, a column that left gives its share
 * to the rest, and a new column takes an even share of the width.
 */
function bandColumns(
    band: PanelBand | undefined,
    before: ReadonlyArray<PanelKind>,
    after: ReadonlyArray<PanelKind>
): number[] {
    const kept = after.map((kind) => {
        const index = before.indexOf(kind);
        return index === -1 ? undefined : (band?.columnPercents[index] ?? 0);
    });
    const keptTotal = kept.reduce<number>((sum, size) => sum + (size ?? 0), 0);
    const joining = kept.filter((size) => size === undefined).length;
    if (joining === 0 || keptTotal === 0) {
        return keptTotal > 0
            ? kept.map((size) => ((size ?? 0) * 100) / keptTotal)
            : evenPercents(after.length);
    }
    const share = 100 / after.length;
    const remaining = 100 - share * joining;
    return kept.map((size) =>
        size === undefined ? share : (size * remaining) / keptTotal
    );
}

/** One entry of a placement plan: kinds hung in one row's band, in order. */
interface PanelPlacement {
    /**
     * The row that hosts the kinds. `undefined`, a row the layout lacks or
     * an unbound row puts them nowhere.
     */
    readonly rowIndex: number | undefined;
    readonly kinds: ReadonlyArray<PanelKind>;
}

/**
 * The bands of every row at once. Entries naming one row are merged in
 * plan order; a row in no entry loses its band; a kind in two entries is a
 * programming error.
 */
export type PanelPlacementPlan = ReadonlyArray<PanelPlacement>;

/**
 * Places the panel panes of a plan: each row named hangs the kinds its
 * entries list in its band, in the order given, and every other row loses
 * its band. Within a row, a kind it already hosts keeps its pane and its
 * id; a kind it does not host joins the band as a new column, keeping the
 * id of the pane it had in another row; a pane of a kind not listed leaves
 * the band, and the band leaves with its last panel. A row that gains its
 * first panel takes the height of a band another row lets go of in the same
 * placement, so a band moving between rows keeps its height; its column
 * sizes are dealt even again. An entry with no row, a row the layout lacks
 * or an unbound row puts its kinds nowhere. A kind listed in two entries
 * throws. The layout itself comes back when nothing has to change.
 */
export function placePanels(
    layout: Layout,
    plan: PanelPlacementPlan,
    createId: ViewportIdFactory
): Layout {
    const kindsByRow = new Map<number, PanelKind[]>();
    const planned = new Set<PanelKind>();
    for (const { rowIndex, kinds } of plan) {
        for (const kind of kinds) {
            if (planned.has(kind)) {
                throw new Error(
                    `placePanels: the ${kind} panel is planned for two rows`
                );
            }
            planned.add(kind);
        }
        if (rowIndex === undefined || !layout.rows[rowIndex]?.dataset) {
            continue;
        }
        const hosted = kindsByRow.get(rowIndex) ?? [];
        hosted.push(...kinds);
        kindsByRow.set(rowIndex, hosted);
    }
    const cellsByKind = new Map<PanelKind, ViewportCell>();
    const releasedBands: PanelBand[] = [];
    layout.rows.forEach((row, index) => {
        panelCellsOf(row).forEach((cell) => {
            const kind = panelKindOfCell(cell);
            if (kind !== undefined) {
                cellsByKind.set(kind, cell);
            }
        });
        if (row.band && (kindsByRow.get(index)?.length ?? 0) === 0) {
            releasedBands.push(row.band);
        }
    });
    let changed = false;
    const rows = layout.rows.map((row, index) => {
        const kinds = kindsByRow.get(index) ?? [];
        if (kinds.length === 0) {
            const stripped = withoutBand(row);
            changed ||= stripped !== row;
            return stripped;
        }
        const had = panelCellsOf(row);
        const hadKinds = had.flatMap((cell) => {
            const kind = panelKindOfCell(cell);
            return kind === undefined ? [] : [kind];
        });
        const unchanged =
            hadKinds.length === kinds.length &&
            hadKinds.every((kind, at) => kind === kinds[at]) &&
            row.band !== undefined;
        if (unchanged) {
            return row;
        }
        changed = true;
        const band = row.band ?? releasedBands.shift();
        const panels = kinds.map((kind, at): ViewportCell => {
            const existing =
                had.find((cell) => panelKindOfCell(cell) === kind) ??
                cellsByKind.get(kind);
            return {
                id: existing?.id ?? createId(),
                content: panelContent(kind),
                placement: {
                    colStart: at + 1,
                    colSpan: 1,
                    rowStart: 1,
                    rowSpan: 1,
                },
            };
        });
        return {
            ...row,
            cells: [...imageCellsOf(row), ...panels],
            band: {
                heightPercent: band?.heightPercent ?? PANEL_BAND_HEIGHT_PERCENT,
                columnPercents: bandColumns(row.band, hadKinds, kinds),
            },
        };
    });
    return changed ? { rows } : layout;
}
